// Prompt-ready rendering of a MULTI-VALUE (distribution) forecast — the PMF itself plus the
// distribution-shaped reasoning behind it. The binary counterpart is `reportContext.js`.
//
// WHY THIS EXISTS. The chat classifier's forecast block was written for binary forecasts: it
// emitted `Probability: NN%` from `contract.probability` and read `report.reasons_for/against`.
// A multi-value forecast has NEITHER — it carries a `forecast_value` PMF, a `multi_report`
// (deliberately not `report`, so it never trips the modal's Yes/No render path), and a
// `distribution_reasoning` block of per-direction drivers. So on a multiway forecast Sayer was
// handed a meaningless scalar (usually 0%) and none of the outcome labels: it could not name
// the leading outcome, let alone say why it led. Everything below was already on the run and
// already rendered by the report modal; only the chat prompt never read it.
//
// The three-location lookup (top-level, then `contract`, then `serverContext`) mirrors
// `ForecastReportModal` exactly — the fresh-submit, contract-embedded, and reload shapes each
// put these blocks in a different place, and a reader that checks one finds nothing on the
// other two paths.
//
// Pure: no React, no store.

import { bucketLabel, extractMultiway, leadingRow } from "./multiway.js";
import { clip, fmtItems } from "./promptLines.js";

/** First present value of `key` across the run's three carrier locations, else null. */
const pick3 = (run, key) => run?.[key] || run?.contract?.[key] || run?.serverContext?.[key] || null;

/** A distribution driver's text field. Drivers are `{text, strength}`, not `{claim}`. */
const driverText = (d) => d?.text ?? d?.claim ?? d;

/**
 * @typedef {object} ForecastDistributionContext
 * @property {boolean} isDistribution false for a binary forecast — callers render the
 *   binary report block instead. Everything below is "" / null when false.
 * @property {string} kind         "numeric" | "categorical" (the outcome-space kind)
 * @property {string} buckets      "  - Label: 42%" lines, display order, or ""
 * @property {string} leading      "Label (42%)" for the highest-probability outcome, or ""
 * @property {string} shape        one-line concentration read ("concentrated" / "spread"), or ""
 * @property {string} summary      multi_report.forecast_summary (analyst voice), or ""
 * @property {string} considerations "  - Title: detail" lines, or ""
 * @property {string} perOutcome   "  - Label: note" lines for notable outcomes, or ""
 * @property {string} driversFor   drivers toward the leader / a higher value, or ""
 * @property {string} driversAgainst drivers toward the field / a lower value, or ""
 * @property {string} spreadDrivers what widens the distribution (numeric only), or ""
 * @property {string} referenceClass the outside-view anchor (numeric only), or ""
 * @property {string} confidence   the forecaster's own confidence note, or ""
 * @property {string} sources      "  - source" lines, or ""
 * @property {string} forLabel     heading for driversFor, keyed to `kind`
 * @property {string} againstLabel heading for driversAgainst, keyed to `kind`
 */

/** The empty shape, returned for every binary forecast. */
const EMPTY = {
  isDistribution: false,
  kind: "",
  buckets: "",
  leading: "",
  shape: "",
  summary: "",
  considerations: "",
  perOutcome: "",
  driversFor: "",
  driversAgainst: "",
  spreadDrivers: "",
  referenceClass: "",
  confidence: "",
  sources: "",
  forLabel: "",
  againstLabel: "",
};

/**
 * Build the distribution context strings from a run.
 *
 * Returns `{isDistribution: false, ...}` for a binary forecast (or anything without a PMF) —
 * `extractMultiway` already excludes a BooleanSpace, so a binary run expressed as its
 * two-slot PMF is correctly NOT treated as a distribution here.
 *
 * @param {any} run a store run (the "active" forecast), or null
 * @param {{ maxBuckets?: number, maxDrivers?: number, maxConsiderations?: number,
 *           maxPerOutcome?: number, maxSources?: number, textLen?: number,
 *           summaryLen?: number }} [opts]
 * @returns {ForecastDistributionContext}
 */
export function forecastDistributionContext(
  run,
  {
    maxBuckets = 24,
    maxDrivers = 12,
    maxConsiderations = 8,
    maxPerOutcome = 8,
    maxSources = 12,
    textLen = 400,
    summaryLen = 2000,
  } = {},
) {
  // `extractMultiway` already checks the run AND its contract for both `forecast_value` and
  // `outcome_labels`, so one call covers the fresh-submit and contract-embedded shapes.
  const mw = extractMultiway(run);
  if (!mw || !Array.isArray(mw.buckets) || mw.buckets.length === 0) return EMPTY;

  const mr = pick3(run, "multi_report") || {};
  const dr = pick3(run, "distribution_reasoning") || {};
  const kind = mw.kind === "numeric" ? "numeric" : "categorical";

  // Buckets come from extractMultiway already in DISPLAY order (a nominal categorical ranked
  // by probability, an ordered/numeric space in authored order), so they are emitted as-is —
  // re-sorting here would put a numeric distribution's bins out of sequence and make the
  // shape read as noise.
  const shown = mw.buckets.slice(0, maxBuckets);
  const buckets = shown
    .map((b) => `  - ${clip(bucketLabel(b), 120)}: ${Math.round((b.p || 0) * 100)}%`)
    .join("\n");
  const omitted = mw.buckets.length - shown.length;

  // The leader is read from the whole PMF, never from `shown` — a truncated tail must not be
  // able to change which outcome is reported as leading.
  const top = leadingRow(mw.buckets);
  const topPct = Math.round((top?.p || 0) * 100);
  const leading = top ? `${clip(bucketLabel(top), 120)} (${topPct}%)` : "";
  // A one-line concentration read, so the model does not have to infer distribution shape by
  // eyeballing a bucket list. The threshold is descriptive only — it labels nothing the
  // percentages don't already say, and exists because "is this a confident forecast?" is a
  // question users ask in words, not in bins.
  const shape = top
    ? topPct >= 60
      ? `concentrated on the leader (${topPct}%)`
      : topPct >= 35
        ? `moderately favours the leader (${topPct}%)`
        : `spread across outcomes (leader only ${topPct}%)`
    : "";

  // Direction framing keyed off the outcome-space kind — mirrors the modal's mwHigherLabel /
  // mwLowerLabel and the server's _MULTI_ARGUMENT_HEADERS. A numeric distribution's drivers
  // push the value higher/lower; a categorical one's push toward or away from the leader.
  const forLabel =
    kind === "numeric"
      ? "Factors favouring a HIGHER outcome"
      : "Factors favouring the LEADING outcome";
  const againstLabel =
    kind === "numeric" ? "Factors favouring a LOWER outcome" : "Factors favouring the FIELD";

  // A numeric question fills drivers_higher/drivers_lower; a categorical one fills `drivers`
  // (toward the leader) and `drivers_against` (toward the field). Same normalisation the modal
  // does, so chat and report cannot disagree about which side a driver is on.
  const forArr = dr.drivers_higher?.length ? dr.drivers_higher : dr.drivers || [];
  const againstArr = dr.drivers_lower?.length ? dr.drivers_lower : dr.drivers_against || [];
  const driverOpts = { max: maxDrivers, len: textLen, strength: true, cites: 2, pick: driverText };

  const srcArr = Array.isArray(mr.sources) ? mr.sources : run?.contract?.sources || [];

  return {
    isDistribution: true,
    kind,
    buckets:
      omitted > 0 ? `${buckets}\n  (+${omitted} lower-probability outcomes omitted)` : buckets,
    leading,
    shape,
    summary: clip(mr.forecast_summary, summaryLen),
    considerations: fmtItems(mr.key_considerations, {
      max: maxConsiderations,
      len: textLen,
      pick: (k) => (k?.title ? `${k.title}: ${k.detail ?? ""}` : (k?.detail ?? k)),
    }),
    perOutcome: fmtItems(mr.per_outcome, {
      max: maxPerOutcome,
      len: textLen,
      pick: (o) => (o?.label ? `${o.label}: ${o.note ?? ""}` : (o?.note ?? o)),
    }),
    driversFor: fmtItems(forArr, driverOpts),
    driversAgainst: fmtItems(againstArr, driverOpts),
    spreadDrivers: fmtItems(dr.spread_drivers, { ...driverOpts, max: maxDrivers }),
    referenceClass: clip(dr.reference_class, textLen),
    confidence: clip(dr.confidence, textLen),
    sources: fmtItems(srcArr, {
      max: maxSources,
      len: 200,
      pick: (s) =>
        typeof s === "string"
          ? s
          : [s?.title ?? s?.name ?? "", s?.url ?? s?.source_url ?? ""].filter(Boolean).join(" "),
    }),
    forLabel,
    againstLabel,
  };
}
